import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { ArrowLeft, Plus } from '@bloo-team/ui/icons'
import { canDissociate, isPrimaryOrg, isProvisioned, nextAssociation, PRIMARY } from '@bloo-team/api-client'
import type { TenantOrg } from '@bloo-team/api-client'
import { Pagination } from '@bloo-team/ui'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { useSettingsStrip } from '@bloo-team/core-shell'
import { SlideOver } from '@bloo-team/ui'
import { useOrganizations } from '../../../lib/organizations'
import {
  useAssociateOrg,
  useDissociateOrg,
  useTenantDetail,
  useTenantOrgs,
} from '../../../lib/tenants'

/**
 * One tenant, and the organizations that reach it
 * (docs/settings-requirements.md §7.4).
 *
 * This page is the only place the organization ↔ tenant relationship can be
 * CHANGED. Both endpoints are keyed by cluster, so the Organizations detail
 * page can show the same edge and not touch it.
 *
 * Two rules the screen has to get right, and both are about not guessing:
 *
 *   the kind is DERIVED   the first organization added becomes the tenant's
 *                         PRIMARY and every later one a SECONDARY. Nobody
 *                         picks. The legacy screen derived it from the length
 *                         of the page it had rendered, which is 0 while
 *                         loading and 0 for an empty first page of a longer
 *                         list — so it could claim to be creating a primary
 *                         for a tenant that already had one. It is derived
 *                         from the server's TOTAL here, and the control is not
 *                         offered until that total has arrived.
 *   the primary is fixed  only a secondary can be removed. There is no
 *                         endpoint that replaces a primary, so a screen that
 *                         offered to remove one would be offering to strand
 *                         the tenant.
 */

/**
 * How many associations the "what does this tenant already have" read asks
 * for. Finite because the endpoint is paged; large enough that a tenant
 * exceeding it is not a deployment this console has seen.
 */
const WHOLE_PAGE = 500

function TenantPage() {
  const { clusterId, scopeId, tenantId } = Route.useParams()

  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(25)
  const [adding, setAdding] = useState(false)
  const [pick, setPick] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [orgQuery, setOrgQuery] = useState('')
  const [confirm, setConfirm] = useState<TenantOrg | null>(null)
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1) }, 250)
    return () => clearTimeout(t)
  }, [search])
  useEffect(() => {
    const t = setTimeout(() => setOrgQuery(orgSearch), 250)
    return () => clearTimeout(t)
  }, [orgSearch])

  const detail = useTenantDetail(tenantId)
  const orgs = useTenantOrgs({ clusterId: tenantId, page, size, query })
  /**
   * A SECOND association read: UNFILTERED, UNPAGED, and the only one allowed
   * to answer "what does this tenant already have".
   *
   * The table's read is both paged and SEARCHED, so its `total` is a count for
   * the current query. Deriving from it means typing a search that matches
   * nothing makes the count 0 — and 0 means PRIMARY, the one association that
   * cannot be undone. It would also leave the duplicate filter blind to any
   * organization on a later page.
   *
   * Same shape as the organization disable confirm's own read (§6.6) and
   * `useOrgResolution`: when a decision needs the whole set, ask for the whole
   * set rather than reusing the list somebody is browsing.
   */
  const whole = useTenantOrgs({ clusterId: tenantId, page: 1, size: WHOLE_PAGE, query: '' })
  // The directory, for the picker — the same one Organizations owns.
  const directory = useOrganizations(orgQuery, adding, 200)
  const associate = useAssociateOrg()
  const dissociate = useDissociateOrg()

  const tenant = detail.data ?? null
  const rows = orgs.data?.orgs ?? []
  const total = orgs.data?.total ?? 0

  /**
   * The kind the next association will be, or null while it is unknowable.
   *
   * From the UNFILTERED read: undefined until it lands, and `nextAssociation`
   * answers null for that rather than guessing 0 — which would mean "primary",
   * the one answer that is structurally irreversible.
   */
  const nextKind = nextAssociation(whole.data?.total)
  const canAdd = nextKind !== null && !associate.isPending

  // Already-attached organizations are not offered again. Built from the WHOLE
  // read, not the page on screen — otherwise an organization on page two is
  // offered, and the operator picks something the server will refuse.
  const attached = useMemo(
    () => new Set((whole.data?.orgs ?? []).map((o) => o.orgId)),
    [whole.data?.orgs],
  )
  const options = (directory.data?.orgs ?? []).filter((o) => !attached.has(o.orgId))

  const busy = associate.isPending || dissociate.isPending
  const actionError = associate.error ?? dissociate.error
  const confirmation = actionError ? null : done

  const runAdd = () => {
    if (!pick || !nextKind) return
    const name = options.find((o) => o.orgId === pick)?.orgName ?? 'The organization'
    associate.mutate(
      { clusterId: tenantId, orgId: pick, association: nextKind },
      {
        onSuccess: () => {
          setDone(`${name} was added as this tenant's ${nextKind} organization.`)
          setAdding(false)
          setPick('')
        },
      },
    )
  }

  const runRemove = () => {
    if (!confirm) return
    dissociate.mutate(
      { clusterId: tenantId, orgId: confirm.orgId },
      {
        onSuccess: () => {
          setDone(`${confirm.orgName} no longer reaches this tenant.`)
          setConfirm(null)
        },
      },
    )
  }

  useSettingsStrip({
    title: tenant?.clusterName ?? (detail.isLoading ? 'Loading…' : 'Tenant'),
    end: (
      <Link
        to="/$clusterId/$scopeId/settings/tenants"
        params={{ clusterId, scopeId }}
        className="bui-btn bui-btn-outline bui-btn-sm"
      >
        <Icon icon={ArrowLeft} size={12} /> All tenants
      </Link>
    ),
  })

  return (
    <div className="app-stack app-stack-wide">
      {detail.error ? <p className="sy-error" role="alert">{detail.error.message}</p> : null}

      {tenant ? (
        <section className="hud-panel sy-card" aria-label="Tenant">
          <div className="sy-pane-head">
            <span className="hud-eyebrow">Overview</span>
            <span className="sy-pill" data-tone={isProvisioned(tenant) ? 'good' : 'warn'}>
              {tenant.state || 'unknown'}
            </span>
          </div>

          <dl className="kv">
            <dt>Licence</dt>
            <dd>{tenant.licenseType || <span className="muted">—</span>}</dd>

            <dt>Core</dt>
            <dd className="mono">
              {tenant.coreList.length
                ? tenant.coreList.map((c) => c.coreIP).filter(Boolean).join(', ') || <span className="muted">—</span>
                : <span className="muted">—</span>}
            </dd>

            <dt>Tenant id</dt>
            <dd className="mono">{tenant.clusterId}</dd>
          </dl>

          <p className="sy-help">
            Renaming a tenant, completing its setup, and granting people access to it are not done
            from this console.
          </p>
        </section>
      ) : null}

      <section className="hud-panel sy-card" aria-label="Organizations">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Organizations</span>
          <span className="mono muted">{total}</span>
          <Button
            variant="outline" size="sm"
            isDisabled={!canAdd}
            onPress={() => { associate.reset(); setDone(null); setPick(''); setAdding(true) }}
          >
            <Icon icon={Plus} size={13} />
            {' '}
            {/* The label states which kind this will create, because nobody
                chooses it and the primary cannot be undone. */}
            {nextKind === PRIMARY ? 'Add primary organization' : 'Add organization'}
          </Button>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search organizations on this tenant"
            value={search}
            onChange={setSearch}
            placeholder="Search by name…"
          />
        </div>

        {orgs.error ? <p className="sy-error" role="alert">{orgs.error.message}</p> : null}

        {!rows.length && !orgs.isLoading && !orgs.error ? (
          <p className="sy-help">
            {query
              ? `No organization on this tenant matches “${query}”.`
              : 'No organization reaches this tenant yet. The first one added becomes its primary.'}
          </p>
        ) : null}

        {rows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Association</th>
                  <th className="num">Active users</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const primary = isPrimaryOrg(o)
                  return (
                    <tr key={o.orgId}>
                      <td>{o.orgName || <span className="muted">—</span>}</td>
                      <td>
                        <span className="sy-pill" data-tone={primary ? 'good' : undefined}>
                          {primary ? 'primary' : 'secondary'}
                        </span>
                      </td>
                      <td className="num">
                        {o.activeUsers != null ? o.activeUsers : <span className="muted">—</span>}
                      </td>
                      <td>
                        {canDissociate(o) ? (
                          <Button
                            variant="ghost" size="sm" tone="warn"
                            aria-label={`Remove ${o.orgName} from this tenant`}
                            isDisabled={busy}
                            onPress={() => { dissociate.reset(); setDone(null); setConfirm(o) }}
                          >
                            Remove
                          </Button>
                        ) : (
                          // No control at all for the primary, rather than a
                          // disabled one: it is not temporarily unavailable,
                          // there is no endpoint that could do it.
                          <span className="muted">structural</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > 0 ? (
          <Pagination
            label="Organization pagination"
            page={page}
            pageSize={size}
            total={total}
            busy={orgs.isFetching}
            unit="organizations"
            onPageChange={setPage}
            onPageSizeChange={(n) => { setSize(n); setPage(1) }}
          />
        ) : null}

        {confirmation ? <p className="sy-test sy-test-ok" role="status">{confirmation}</p> : null}
        {actionError ? <p className="sy-error" role="alert">{actionError.message}</p> : null}

        {/* A SlideOver, not another hand-rolled strip: this is a
            pick-and-submit form, and that is what OrgForm and UserForm are.
            The strip is for confirms. */}
        <SlideOver
          open={adding}
          onClose={() => setAdding(false)}
          title="Add an organization"
          eyebrow="Settings · Tenants"
        >
          <div className="sy-form">
            <div className="sy-field">
              <TextField
                aria-label="Search the organization directory"
                value={orgSearch}
                onChange={setOrgSearch}
                placeholder="Search organizations…"
              />
              <label className="sy-field-label" htmlFor="tn-org">Organization</label>
              <select id="tn-org" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">{directory.isLoading ? 'Loading…' : 'Select an organization…'}</option>
                {options.map((o) => (
                  <option key={o.orgId} value={o.orgId}>
                    {o.orgName}{o.enabled ? '' : ' (disabled)'}
                  </option>
                ))}
              </select>
              <p className="sy-help">
                Organizations already on this tenant are not listed.
              </p>
              <p className={nextKind === PRIMARY ? 'sy-warning' : 'sy-help'}>
                {nextKind === PRIMARY
                  ? 'This tenant has no organizations, so this one becomes its PRIMARY. A primary cannot be removed or swapped afterwards.'
                  : 'This tenant already has a primary organization, so this one is added as a secondary.'}
              </p>
            </div>

            {associate.error ? (
              <p className="sy-error" role="alert">{associate.error.message}</p>
            ) : null}

            <div className="sy-controls">
              <Button variant="ghost" size="sm" onPress={() => setAdding(false)}>Cancel</Button>
              <Button
                variant="solid" size="sm"
                isDisabled={!pick || !canAdd}
                onPress={runAdd}
              >
                {associate.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </div>
        </SlideOver>

        {confirm ? (
          <ConfirmStrip
            label={`Remove ${confirm.orgName} from this tenant?`}
            message={`Remove ${confirm.orgName} from ${tenant?.clusterName ?? 'this tenant'}? Its ${confirm.activeUsers ?? 0} active ${confirm.activeUsers === 1 ? 'user' : 'users'} lose access to this tenant's data. The organization itself is not deleted.`}
            confirmLabel="Remove"
            cancelLabel="Keep"
            tone="warn"
            busy={busy}
            onCancel={() => setConfirm(null)}
            onConfirm={runRemove}
          />
        ) : null}

        <p className="sy-help">
          This is the only place the organization–tenant connection can be changed. An
          organization's own page shows which tenants it reaches, read-only, because both of these
          endpoints are addressed by tenant.
        </p>
      </section>
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/tenants_/$tenantId')({
  component: TenantPage,
})
